// DMテンプレートの管理（作成・編集・削除）。旧SPA app.js renderTemplates の移植。
// 本文は3000字まで保存可（gifting /api/templates）。DM送信は1000字上限なので、超えるテンプレには注意を出す。
import { useCallback, useEffect, useState } from "react";
import { createGfTemplate, deleteGfTemplate, fetchGfTemplates, updateGfTemplate } from "../../../api/giftingClient";
import type { GfTemplate, Loadable } from "../../../types/gifting";
import { errMsg } from "../gfUtil";
import { DM_MAX_CHARS } from "../../../lib/giftingRules";
import { GfButton, GfErrorBanner, GfField, GfModal, GfSkeleton } from "../ui/GfUi";
import { ConfirmModal } from "./ConfirmModal";

const NAME_MAX = 100;
const BODY_MAX = 3000;
const PLACEHOLDERS = "{名前} {商品} {フォームURL} {期限}";

interface Draft {
  name: string;
  body: string;
}

function lengthHint(body: string) {
  const n = body.length;
  if (n > BODY_MAX) return <span className="gf-dm-count is-over">{n} / {BODY_MAX}字（保存できません）</span>;
  if (n > DM_MAX_CHARS) return <span className="gf-dm-count is-over">{n}字（DM送信は{DM_MAX_CHARS}字まで。コピー用なら可）</span>;
  return <span className="gf-dm-count">{n}字</span>;
}

export function TemplatesModal({ open, onClose, toast }: { open: boolean; onClose: () => void; toast: (m: string) => void }) {
  const [list, setList] = useState<Loadable<GfTemplate[]>>({ status: "loading" });
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [del, setDel] = useState<GfTemplate | null>(null);
  const [newDraft, setNewDraft] = useState<Draft>({ name: "", body: "" });
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const rows = [...(await fetchGfTemplates())].sort((a, b) => a.sort_order - b.sort_order);
      setList({ status: "ok", data: rows });
      setDrafts(Object.fromEntries(rows.map((t) => [t.id, { name: t.name, body: t.body }])));
    } catch (e) {
      setList({ status: "error", message: errMsg(e) });
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setList({ status: "loading" });
    setNewDraft({ name: "", body: "" });
    setError(null);
    void load();
  }, [open, load]);

  const valid = (d: Draft) => {
    if (!d.name.trim()) return "テンプレート名を入力してください";
    if (!d.body.trim()) return "本文を入力してください";
    if (d.name.trim().length > NAME_MAX) return `テンプレート名は${NAME_MAX}字までです`;
    if (d.body.length > BODY_MAX) return `本文は${BODY_MAX}字までです`;
    return null;
  };

  const save = async (t: GfTemplate) => {
    const d = drafts[t.id];
    const v = d ? valid(d) : "入力がありません";
    if (v) return setError(v);
    setBusyId(t.id);
    setError(null);
    try {
      await updateGfTemplate(t.id, { name: d.name.trim(), body: d.body });
      toast("テンプレートを保存しました");
      await load();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusyId(null);
    }
  };

  const create = async () => {
    const v = valid(newDraft);
    if (v) return setError(v);
    setBusyId("new");
    setError(null);
    try {
      const max = list.status === "ok" ? Math.max(0, ...list.data.map((t) => t.sort_order)) : 0;
      await createGfTemplate({ name: newDraft.name.trim(), body: newDraft.body, sort_order: max + 1 });
      toast("テンプレートを追加しました");
      setNewDraft({ name: "", body: "" });
      await load();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusyId(null);
    }
  };

  const remove = async () => {
    if (!del) return;
    setBusyId(del.id);
    try {
      await deleteGfTemplate(del.id);
      toast("テンプレートを削除しました");
      setDel(null);
      await load();
    } catch (e) {
      toast(`削除できませんでした: ${errMsg(e)}`);
    } finally {
      setBusyId(null);
    }
  };

  const rows = list.status === "ok" ? list.data : [];

  return (
    <>
      <GfModal open={open} title="DMテンプレート" width={640} onClose={del ? () => undefined : onClose} footer={<GfButton onClick={onClose}>閉じる</GfButton>}>
        <div className="gf-note">
          エントリーの「DM／メッセージ」で挿入すると <span className="gf-mono">{PLACEHOLDERS}</span> が自動で置き換わります（フォームURLが無効のときは「（フォームURLなし）」）。
        </div>
        {error && <GfErrorBanner message={error} />}
        {list.status === "loading" && <GfSkeleton height={160} />}
        {list.status === "error" && <GfErrorBanner message={`テンプレートを読み込めませんでした。${list.message}`} onRetry={() => void load()} />}
        {list.status === "ok" && rows.length === 0 && <div className="gf-note">まだテンプレートがありません。</div>}
        {rows.map((t) => {
          const d = drafts[t.id] ?? { name: t.name, body: t.body };
          const dirty = d.name !== t.name || d.body !== t.body;
          return (
            <div className="gf-boxed gf-tpl-item" key={t.id}>
              <GfField label="テンプレート名">
                <input
                  className="gf-input"
                  value={d.name}
                  maxLength={NAME_MAX}
                  onChange={(e) => setDrafts((m) => ({ ...m, [t.id]: { ...d, name: e.target.value } }))}
                />
              </GfField>
              <GfField label="本文" hint={lengthHint(d.body)}>
                <textarea
                  className="gf-textarea"
                  rows={6}
                  value={d.body}
                  onChange={(e) => setDrafts((m) => ({ ...m, [t.id]: { ...d, body: e.target.value } }))}
                />
              </GfField>
              <div className="gf-inline-row is-tight gf-tpl-actions">
                <GfButton size="sm" variant="primary" disabled={!dirty || busyId !== null} onClick={() => void save(t)}>
                  {busyId === t.id ? "保存中…" : "保存"}
                </GfButton>
                <button type="button" className="gf-link-btn is-danger" disabled={busyId !== null} onClick={() => setDel(t)}>
                  削除
                </button>
              </div>
            </div>
          );
        })}

        {list.status === "ok" && (
          <div className="gf-panel-section">
            <span className="gf-panel-section-title">新規テンプレート</span>
            <GfField label="テンプレート名">
              <input
                className="gf-input"
                value={newDraft.name}
                maxLength={NAME_MAX}
                placeholder="例）初回のご挨拶"
                onChange={(e) => setNewDraft({ ...newDraft, name: e.target.value })}
              />
            </GfField>
            <GfField label="本文" hint={lengthHint(newDraft.body)}>
              <textarea
                className="gf-textarea"
                rows={6}
                value={newDraft.body}
                placeholder={`{名前}さん、はじめまして。STOQ の…`}
                onChange={(e) => setNewDraft({ ...newDraft, body: e.target.value })}
              />
            </GfField>
            <div>
              <GfButton size="sm" variant="primary" disabled={busyId !== null} onClick={() => void create()}>
                {busyId === "new" ? "追加中…" : "追加する"}
              </GfButton>
            </div>
          </div>
        )}
      </GfModal>
      <ConfirmModal
        open={del !== null}
        title="テンプレートを削除"
        confirmLabel="削除する"
        danger
        busy={busyId !== null}
        onConfirm={() => void remove()}
        onClose={() => setDel(null)}
      >
        <p>「{del?.name}」を削除しますか。元に戻せません。</p>
      </ConfirmModal>
    </>
  );
}
